import { DSCircularButton } from "@components/design-system/molecules";
import { DSNoteInput } from "@components/design-system/organisms/DSNoteInput";
import { useCompanion } from "@hooks/use-companion";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFormContext, useWatch } from "react-hook-form";

import { UserPermissions } from "../../../../../hooks/use-premium-feature";
import Twemoji from "../../../../../Twemoji";
import CascadingDropdown from "../../../../common/menus/CascadingDropdown";
import PremiumFeatureGate from "../../../../common/premium/PremiumFeatureGate";
import UpgradeModal from "../../../../common/premium/UpgradeModal";
import CustomizationFeedback from "../../../common/CustomizationFeedback";

export function EditClientSummaryResult({
  control,
  noteUuid,
  aiAssistantEnabled,
  languageAiAssistantEnabled,
  subheadingsAiAssistantEnabled,
}) {
  const { getValues, setValue } = useFormContext();
  const { writingCompanion } = useFlags();
  const { expandSidebar, createNewSession, creatingSession } =
    useCompanion();

  const clientSummary = useWatch({ control, name: "clientSummary" });

  const wa = useWritingAssistant({
    noteUuid,
    fieldName: "clientSummary",
    getFieldValue: () => getValues("clientSummary"),
    setFieldValue: (v) =>
      setValue("clientSummary", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  // Map clientSummary form field to DSNoteInput's freeform "content" field
  const noteData = { content: clientSummary || "" };

  const handleChange = (newData) => {
    if (newData.content !== clientSummary) {
      setValue("clientSummary", newData.content, {
        shouldDirty: true,
      });
    }
  };

  const aiActions =
    aiAssistantEnabled && !wa.diffState ? (
      <>
        <PremiumFeatureGate
          featureName="Writing Assistant"
          requiredPermission={
            UserPermissions.WRITING_ASSISTANT_ENABLED
          }
          iconPosition="center"
          iconSize="xs"
        >
          <CascadingDropdown
            options={wa.options}
            setIsMenuOpen={wa.setIsMenuOpen}
            title="Client Summary"
            isTriggerComponentDisabled={
              wa.loadingBeforeAndDuringStreaming
            }
            triggerComponent={({ onClick }) => (
              <DSCircularButton
                onClick={onClick}
                size="sm"
                disabled={wa.loadingBeforeAndDuringStreaming}
                title="Trigger Writing Assistant"
                dataTestId="cascading-dropdown-trigger"
              >
                <Twemoji className="text-xl z-20" emoji="✨" />
              </DSCircularButton>
            )}
          />
        </PremiumFeatureGate>
        {writingCompanion && (
          <PremiumFeatureGate
            featureName="Companion"
            requiredPermission={undefined}
            iconPosition="center"
            iconSize="xs"
          >
            <DSCircularButton
              size="sm"
              disabled={creatingSession}
              onClick={() => {
                if (creatingSession) return;
                expandSidebar();
                createNewSession();
              }}
              title="Edit with Quill"
              dataTestId="edit-with-quill-trigger"
            >
              <Twemoji className="text-xl z-20" emoji="🦔" />
            </DSCircularButton>
          </PremiumFeatureGate>
        )}
      </>
    ) : undefined;

  return (
    <>
      <UpgradeModal
        shouldShow={wa.showUpgradeModal}
        setShouldShow={wa.setShowUpgradeModal}
        featureName="Writing Assistant"
      />

      <DSNoteInput
        data={noteData}
        onChange={handleChange}
        dataTestId="client-summary-input"
      >
        {wa.diffState ? (
          <DSNoteInput.NoteField
            section="freeform"
            showSparkleButton={false}
            showCopyButton
            actions={aiActions}
            diffState={wa.diffState}
            onDiffAccept={wa.acceptChanges}
            onDiffReject={wa.rejectChanges}
          />
        ) : (
          <DSNoteInput.NoteField
            section="freeform"
            showSparkleButton={false}
            showCopyButton
            actions={aiActions}
          />
        )}
      </DSNoteInput>

      {wa.isShowingExtraContent &&
        wa.customizationFeedback.hasFeedbackAccess && (
          <CustomizationFeedback
            llmCustomizationUuid={
              wa.customizationFeedback.llmCustomizationUuid
            }
          />
        )}
    </>
  );
}
